import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Slider,
  Chip,
  TextField,
  Box,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { submitFeedback } from "actions/chat-actions";

const ISSUE_TAGS = [
  { value: "wrong_numbers", label: "Wrong numbers" },
  { value: "hallucinated", label: "Made up data" },
  { value: "outdated_data", label: "Outdated data" },
  { value: "missed_question", label: "Didn't answer my question" },
  { value: "missing_context", label: "Missing breakdown / context" },
  { value: "wrong_time_period", label: "Wrong time period" },
  { value: "too_vague", label: "Too vague" },
  { value: "too_verbose", label: "Too long-winded" },
  { value: "hard_to_read", label: "Hard to read / format" },
  { value: "bad_chart", label: "Wrong or bad chart" },
  { value: "slow", label: "Too slow" },
  { value: "other", label: "Something else" },
];

const DIMENSIONS = [
  { key: "accuracy", label: "Accuracy" },
  { key: "completeness", label: "Completeness" },
  { key: "clarity", label: "Clarity" },
  { key: "visualization", label: "Visualization" },
];

const SLIDER_MARKS = [
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
];

const useStyles = makeStyles(() => ({
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingRight: "8px",
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  titleSubtext: {
    fontSize: "12px",
    color: "#6B7280",
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontWeight: 400,
    marginTop: "2px",
  },
  sectionLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6B7280",
    marginTop: "18px",
    marginBottom: "8px",
    fontFamily: "'DM Sans', sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  starsRow: {
    display: "flex",
    gap: "6px",
    marginBottom: "4px",
  },
  starBtn: {
    background: "none",
    border: "none",
    padding: "2px",
    cursor: "pointer",
    fontSize: "22px",
    lineHeight: 1,
    color: "#D1D5DB",
    outline: "none",
    transition: "transform 120ms ease, color 120ms ease",
    "&:hover": {
      transform: "scale(1.08)",
    },
  },
  starFilled: {
    color: "#F5A623",
  },
  dimensionRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    alignItems: "center",
    gap: "24px",
    marginBottom: "14px",
    paddingRight: "16px",
  },
  dimensionLabel: {
    fontSize: "13px",
    color: "#4B5563",
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
  slider: {
    color: "#2563eb",
    "& .MuiSlider-thumb": {
      boxShadow: "0 0 0 1px rgba(37,99,235,0.2)",
    },
  },
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "4px",
  },
  chipSelected: {
    backgroundColor: "#2563eb !important",
    color: "#fff !important",
    "&:hover": {
      backgroundColor: "#1d4ed8 !important",
    },
  },
  chipUnselected: {
    "&:hover": {
      borderColor: "#9CA3AF !important",
    },
  },
  otherHint: {
    fontSize: "11px",
    color: "#6B7280",
    marginTop: "4px",
    fontFamily: "'IBM Plex Sans', sans-serif",
  },
  field: {
    marginTop: "8px",
  },
  errorAlert: {
    backgroundColor: "#FEF2F2",
    color: "#B91C1C",
    border: "1px solid #FECACA",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: "'IBM Plex Sans', sans-serif",
    marginBottom: "8px",
    width: "100%",
  },
  dialogContent: {
    padding: "20px 24px",
  },
  submitBtn: {
    minWidth: "120px",
  },
}));

const FeedbackDialog = ({ open, onClose, messageId, feedback }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [dimensionValues, setDimensionValues] = useState({
    accuracy: 3,
    completeness: 3,
    clarity: 3,
    visualization: 3,
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [comment, setComment] = useState("");
  const [expectedAnswer, setExpectedAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pre-fill from feedback only when the dialog transitions from closed to open.
  // (Re-running this on `feedback` change would wipe the "Saved!" success state
  // immediately after a successful submit, since the reducer updates `feedback`.)
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setRating(feedback?.rating || 0);
      setHoverRating(0);
      setDimensionValues({
        accuracy: feedback?.accuracy ?? 3,
        completeness: feedback?.completeness ?? 3,
        clarity: feedback?.clarity ?? 3,
        visualization: feedback?.visualization ?? 3,
      });
      setSelectedTags(Array.isArray(feedback?.issue_tags) ? [...feedback.issue_tags] : []);
      setComment(feedback?.comment || "");
      setExpectedAnswer(feedback?.expected_answer || "");
      setSubmitting(false);
      setErrorVisible(false);
      setSuccess(false);
    }
    wasOpenRef.current = open;
  }, [open, feedback]);

  const toggleTag = (tagValue) => {
    setSelectedTags((prev) =>
      prev.includes(tagValue) ? prev.filter((t) => t !== tagValue) : [...prev, tagValue]
    );
  };

  const handleDimensionChange = (key, value) => {
    setDimensionValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setErrorVisible(false);
    const payload = { rating };
    DIMENSIONS.forEach(({ key }) => {
      payload[key] = dimensionValues[key];
    });
    if (selectedTags.length > 0) payload.issue_tags = selectedTags;
    const trimmedComment = comment.trim();
    if (trimmedComment) payload.comment = trimmedComment;
    const trimmedExpected = expectedAnswer.trim();
    if (trimmedExpected) payload.expected_answer = trimmedExpected;

    // Use the underlying API directly so we can await success/failure here.
    // Falling back to dispatch alone makes it hard to show in-dialog errors.
    try {
      const { submitFeedbackAPI } = await import("api/chat-api");
      const response = await submitFeedbackAPI(messageId, payload);
      if (response && response.id) {
        // Update redux store for FeedbackBar
        dispatch({
          type: "SUBMIT_FEEDBACK_SUCCESS",
          messageId,
          response,
        });
        setSuccess(true);
        setSubmitting(false);
        setTimeout(() => {
          onClose();
        }, 900);
      } else {
        setSubmitting(false);
        setErrorVisible(true);
      }
    } catch (e) {
      setSubmitting(false);
      setErrorVisible(true);
    }
  };

  const stars = [1, 2, 3, 4, 5].map((n) => {
    const filled = n <= (hoverRating || rating);
    return (
      <button
        key={n}
        type="button"
        className={`${classes.starBtn} ${filled ? classes.starFilled : ""}`}
        aria-label={`Rate ${n} of 5`}
        onClick={() => setRating(n)}
        onMouseEnter={() => setHoverRating(n)}
        onMouseLeave={() => setHoverRating(0)}
      >
        {filled ? "★" : "☆"}
      </button>
    );
  });

  const renderSubmitContent = () => {
    if (success) {
      return (
        <React.Fragment>
          <CheckCircleOutlineIcon style={{ fontSize: 16, marginRight: 6 }} />
          Saved!
        </React.Fragment>
      );
    }
    if (submitting) {
      return (
        <React.Fragment>
          <CircularProgress size={16} color="inherit" style={{ marginRight: 6 }} />
          Submitting…
        </React.Fragment>
      );
    }
    return "Submit";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="feedback-dialog-title">
      <DialogTitle id="feedback-dialog-title" disableTypography>
        <div className={classes.titleRow}>
          <div className={classes.titleBlock}>
            <Typography variant="h6" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: "16px" }}>
              Give detailed feedback
            </Typography>
            <span className={classes.titleSubtext}>Help us improve this answer.</span>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <div className={classes.sectionLabel}>Overall rating</div>
        <div className={classes.starsRow}>{stars}</div>

        <div className={classes.sectionLabel}>How did the answer do on…</div>
        {DIMENSIONS.map(({ key, label }) => (
          <div key={key} className={classes.dimensionRow}>
            <span className={classes.dimensionLabel}>{label}</span>
            <Slider
              className={classes.slider}
              value={dimensionValues[key]}
              onChange={(_, val) => handleDimensionChange(key, val)}
              min={1}
              max={5}
              step={1}
              marks={SLIDER_MARKS}
              valueLabelDisplay="auto"
              aria-label={`${label} rating`}
            />
          </div>
        ))}

        <div className={classes.sectionLabel}>What went wrong? (optional)</div>
        <Box className={classes.chipsWrap}>
          {ISSUE_TAGS.map((tag) => {
            const selected = selectedTags.includes(tag.value);
            return (
              <Chip
                key={tag.value}
                label={tag.label}
                clickable
                variant={selected ? "default" : "outlined"}
                className={selected ? classes.chipSelected : classes.chipUnselected}
                onClick={() => toggleTag(tag.value)}
              />
            );
          })}
        </Box>
        {selectedTags.includes("other") && (
          <div className={classes.otherHint}>Tell us more in the comment box</div>
        )}

        <div className={classes.sectionLabel}>Comment</div>
        <TextField
          className={classes.field}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder="What did we get wrong, or what could we improve?"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        <div className={classes.sectionLabel}>What would the right answer have been?</div>
        <TextField
          className={classes.field}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder="If the answer was wrong, what should it have been?"
          value={expectedAnswer}
          onChange={(e) => setExpectedAnswer(e.target.value)}
        />
      </DialogContent>

      <DialogActions style={{ padding: "12px 20px", flexDirection: "column", alignItems: "stretch" }}>
        {errorVisible && (
          <div className={classes.errorAlert}>
            Couldn't save — please try again
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button onClick={onClose} disabled={submitting || success}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!rating || submitting || success}
            variant="contained"
            color="primary"
            className={classes.submitBtn}
            style={{ display: "flex", alignItems: "center" }}
          >
            {renderSubmitContent()}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
